import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase';

// カードコンポーネント（変更なし）
const Card = ({ children, className }) => (
  <div className={`bg-white p-6 rounded-lg shadow-sm ${className}`}>
    {children}
  </div>
);

// カテゴリーの定義とバッジコンポーネント（新規追加）
const categories = {
    news: { name: 'お知らせ', color: 'bg-gray-100 text-gray-800' },
    maintenance: { name: 'メンテナンス', color: 'bg-yellow-100 text-yellow-800' },
    incident: { name: '障害情報', color: 'bg-red-100 text-red-800' },
    release: { name: '機能追加', color: 'bg-blue-100 text-blue-800' },
    important: { name: '重要', color: 'bg-red-100 text-red-800' },
};

const CategoryBadge = ({ category }) => {
    const categoryInfo = categories[category] || categories['news'];
    return (
        <span className={`px-2 py-1 mr-3 inline-flex text-xs leading-5 font-semibold rounded-md ${categoryInfo.color}`}>
            {categoryInfo.name}
        </span>
    );
};


const Announcements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setLoading(true);
        const announcementsRef = collection(db, 'announcements');
        const q = query(
          announcementsRef,
          where('status', '==', 'published'),
          orderBy('publishedAt', 'desc'),
          limit(6)
        );

        const documentSnapshots = await getDocs(q);

        if (documentSnapshots.docs.length > 5) {
          setHasMore(true);
        }

        const announcementsData = documentSnapshots.docs.slice(0, 5).map(doc => {
            const data = doc.data();
            const date = data.publishedAt ? new Date(data.publishedAt.seconds * 1000).toLocaleDateString('ja-JP') : '';
            return {
                id: doc.id,
                date: date,
                title: data.title,
                content: data.content,
                category: data.category,
            };
        });
        
        setAnnouncements(announcementsData);
        setError(null);
      } catch (err) {
        console.error("Error fetching announcements:", err);
        setError('お知らせの取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  const renderContent = () => {
    if (loading) return <div className="text-center text-gray-500">読み込み中...</div>;
    if (error) return <div className="text-center text-red-500">{error}</div>;
    if (announcements.length === 0) return <div className="text-center text-gray-500">お知らせはありません</div>;

    // detail, summaryタグでアコーディオン形式に変更
    return (
      <div className="space-y-1">
        {announcements.map((item) => (
          <details key={item.id} className="p-3 border-b border-gray-200 group last:border-b-0">
            <summary className="flex items-center justify-between cursor-pointer list-none">
              <div className="flex items-center">
                <p className="text-sm text-gray-500 w-24 flex-shrink-0">{item.date}</p>
                <CategoryBadge category={item.category} />
                <h3 className="text-sm font-medium text-gray-800 group-hover:text-blue-600">{item.title}</h3>
              </div>
              <div className="transition-transform duration-300 group-open:rotate-90">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-gray-500"><path d="m9 18 6-6-6-6"/></svg>
              </div>
            </summary>
            <div className="mt-3 pl-8">
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.content}</p>
            </div>
          </details>
        ))}
      </div>
    );
  };

  const renderFooter = () => {
    if (hasMore) {
      return (
        <div className="mt-4 text-right">
          {/* target="_blank" を追加して別タブで開く */}
          <a href="/announcements" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-600 hover:text-blue-800">
            もっと見る
          </a>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <h3 className="font-bold text-gray-800 mb-2">お知らせ</h3>
      {renderContent()}
      {renderFooter()}
    </Card>
  );
};

export default Announcements;
